import {
  ActionIcon,
  NumberInput,
  Switch,
  Textarea,
  TextInput,
  Select,
  MultiSelect,
} from "@mantine/core";
import React, { useState, useEffect } from "react";
import TableAttributeModel from "../Table/TableAttributeModel";
import {
  ApiResponseDataType,
  ApiResponseDataValueType,
} from "../common/utilities";
import { IconLetterCaseUpper } from "@tabler/icons-react";
import { getAllTagDetails, getTableGroupNames } from "../api/dynamodb-db-api";
// Using native date input via TextInput (type="date").
// Mantine `DatePicker` typings in this project don't accept `label`/`placeholder`.

interface TableAttributeInputProps {
  value: ApiResponseDataValueType;
  attribute: TableAttributeModel;
  disabled: boolean;
  onChange: (value: ApiResponseDataType) => void;
}

export default function TableAttributeInput({
  value,
  attribute,
  disabled,
  onChange,
}: TableAttributeInputProps) {
  const [capitalise, setCapitalise] = React.useState<boolean>(
    attribute.capitalise
  );
  const [groupNames, setGroupNames] = useState<string[]>([]);
  const [tagNames, setTagNames] = useState<string[]>([]);

  useEffect(() => {
    getGroupNames();
    fetchTagNames();
  }, []);

  const getGroupNames = async () => {
    if (attribute.fieldType === "select" && attribute.name === "groupNumber") {
      const response = await getTableGroupNames("access-codes");
      setGroupNames(response);
    }
  };

  const fetchTagNames = async () => {
    if (attribute.fieldType === "multiSelect" && attribute.name === "tags") {
      const response = await getAllTagDetails("tags");
      const result = response.map((res: any) => res.name);
      setTagNames(result);
    }
  };

  return attribute.fieldType === "multiSelect" && attribute.name === "tags" ? (
    <MultiSelect
      label={`Enter ${attribute.label}`}
      placeholder={attribute.label}
      data={tagNames}
      searchable
      required={attribute.required}
      disabled={disabled}
      value={value as string[]}
      nothingFoundMessage="Nothing found..."
      onChange={(value) =>{
        onChange({
          [attribute.name]: value,
        })
        }
      }
    />
  ) : attribute.fieldType === "select" && attribute.name === "groupNumber" ? (
    <Select
      label={`Enter ${attribute.label}`}
      placeholder={attribute.label}
      data={groupNames}
      value={(value as string) || null}
      searchable
      required={attribute.required}
      disabled={disabled}
      nothingFoundMessage="Nothing found..."
      onChange={(_value: any, option) => {
        onChange({
          [attribute.name]: _value,
        });
      }}
    />
  ) : attribute.type === "string" ? (
    attribute.fieldType === "date" ? (
      <TextInput
        key={attribute.name}
        variant={"filled"}
        label={`Enter ${attribute.label}`}
        required={attribute.required}
        disabled={disabled}
        type={"date"}
        value={(value as string) ?? ""}
        onChange={(element) =>
          onChange({
            [attribute.name]: element.target.value,
          })
        }
      />
    ) : attribute.longText ? (
      <Textarea
        key={attribute.name}
        variant={"filled"}
        placeholder={attribute.label}
        label={`Enter ${attribute.label}`}
        required={attribute.required}
        value={value as string}
        disabled={disabled}
        onChange={(element) =>
          onChange({
            [attribute.name]: element.target.value,
          })
        }
      />
    ) : (
      <TextInput
        key={attribute.name}
        variant={"filled"}
        placeholder={attribute.autoGenerated && !disabled ? "Auto" : attribute.label}
        label={`Enter ${attribute.label}`}
        description={attribute.autoGenerated && !disabled ? "Leave blank to auto-fill, or enter to add manually" : undefined}
        required={attribute.required && !(attribute.autoGenerated && !disabled)}
        value={value as string}
        rightSection={
          attribute.capitalise ? (
            <ActionIcon
              variant={capitalise ? "filled" : "outline"}
              onClick={() => setCapitalise((value) => !value)}
            >
              <IconLetterCaseUpper size={16} />
            </ActionIcon>
          ) : undefined
        }
        disabled={disabled}
        onChange={(element) =>
          onChange({
            [attribute.name]: capitalise
              ? element.target.value.toUpperCase()
              : element.target.value,
          })
        }
      />
    )
  ) : attribute.type === "number" ? (
    <NumberInput
      key={attribute.name}
      variant={"filled"}
      placeholder={attribute.autoGenerated && !disabled ? "Auto" : attribute.label}
      label={`Enter ${attribute.label}`}
      description={attribute.autoGenerated && !disabled ? "Leave blank to auto-fill, or enter to add manually" : undefined}
      allowNegative={false}
      allowDecimal={false}
      allowedDecimalSeparators={[]}
      allowLeadingZeros={false}
      required={attribute.required && !(attribute.autoGenerated && !disabled)}
      value={parseInt(value as string) === -1 ? "" : (value as number)}
      disabled={disabled}
      onChange={(value) =>
        onChange({
          [attribute.name]: value,
        })
      }
    />
  ) : (
    <Switch
      key={attribute.name}
      label={attribute.label}
      checked={value as boolean}
      onChange={(element) =>
        onChange({
          [attribute.name]: element.currentTarget.checked,
        })
      }
    />
  );
}